// Comprehensive error handling utilities

class ErrorHandler {
  // Handle API errors with user-friendly messages
  static handleApiError(error, context = '') {
    console.error(`API Error in ${context}:`, error);
    
    // Network errors
    if (error?.message?.includes('Failed to fetch') || 
        error?.message?.includes('NetworkError') ||
        error?.message?.includes('fetch')) {
      return {
        type: 'network',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        userMessage: 'Connection failed. Please try again.',
        retryable: true
      };
    }
    
    // Authentication errors
    if (error?.message?.includes('Invalid login credentials') ||
        error?.message?.includes('JWT') ||
        error?.message?.includes('unauthorized')) {
      return {
        type: 'auth',
        message: 'Authentication failed. Please sign in again.',
        userMessage: 'Please sign in again to continue.',
        retryable: false,
        action: 'redirect_to_auth'
      };
    }
    
    // Database errors
    if (error?.message?.includes('relation') ||
        error?.message?.includes('column') ||
        error?.message?.includes('constraint')) {
      return {
        type: 'database',
        message: 'Database error occurred. Please try again.',
        userMessage: 'Something went wrong. Please try again.',
        retryable: true
      };
    }
    
    // Rate limiting
    if (error?.message?.includes('rate limit') ||
        error?.message?.includes('too many requests')) {
      return {
        type: 'rate_limit',
        message: 'Too many requests. Please wait a moment.',
        userMessage: 'Please wait a moment before trying again.',
        retryable: true,
        retryAfter: 60
      };
    }
    
    // Generic error
    return {
      type: 'unknown',
      message: error?.message || 'An unexpected error occurred',
      userMessage: 'Something went wrong. Please try again.',
      retryable: true
    };
  }
  
  // Handle content processing errors
  static handleContentError(error, contentType = '') {
    console.error(`Content processing error for ${contentType}:`, error);
    
    if (error?.message?.includes('transcription')) {
      return {
        type: 'transcription',
        message: 'Failed to transcribe content. The content may not be audio/video or may be corrupted.',
        userMessage: 'Unable to transcribe this content. Please try a different file.',
        retryable: false
      };
    }
    
    if (error?.message?.includes('analysis') || error?.message?.includes('AI')) {
      return {
        type: 'analysis',
        message: 'AI analysis failed. The content may not be suitable for analysis.',
        userMessage: 'Unable to analyze this content. Please try again.',
        retryable: true
      };
    }
    
    return this.handleApiError(error, `content processing for ${contentType}`);
  }
  
  // Handle chat errors
  static handleChatError(error, chatContext = '') {
    console.error(`Chat error in ${chatContext}:`, error);
    
    if (error?.message?.includes('context') || error?.message?.includes('sources')) {
      return {
        type: 'context',
        message: 'Failed to load chat context. Some content may not be available.',
        userMessage: 'Unable to load chat context. Please try again.',
        retryable: true
      };
    }
    
    return this.handleApiError(error, `chat ${chatContext}`);
  }
  
  // Handle file upload errors
  static handleUploadError(error, fileName = '') {
    console.error(`Upload error for ${fileName}:`, error);
    
    if (error?.message?.includes('size') || error?.message?.includes('too large')) {
      return {
        type: 'size',
        message: 'File is too large. Please choose a smaller file.',
        userMessage: 'File is too large. Please choose a smaller file.',
        retryable: false
      };
    }
    
    if (error?.message?.includes('format') || error?.message?.includes('type')) {
      return {
        type: 'format',
        message: 'Unsupported file format. Please choose a different file type.',
        userMessage: 'Unsupported file format. Please choose a different file type.',
        retryable: false
      };
    }
    
    return this.handleApiError(error, `file upload for ${fileName}`);
  }
  
  // Show error toast notification
  static showErrorToast(errorInfo, toast) {
    if (toast) {
      toast({
        title: 'Error',
        description: errorInfo.userMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      });
    }
  }
  
  // Show success toast notification
  static showSuccessToast(message, toast) {
    if (toast) {
      toast({
        title: 'Success',
        description: message,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });
    }
  }
  
  // Show loading toast notification
  static showLoadingToast(message, toast) {
    if (toast) {
      return toast({
        title: 'Processing',
        description: message,
        status: 'info',
        duration: null,
        isClosable: false,
        position: 'top-right'
      });
    }
  }
  
  // Retry mechanism with exponential backoff
  static async retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        const errorInfo = this.handleApiError(error);
        
        if (!errorInfo.retryable || attempt === maxRetries) {
          throw error;
        }
        
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`Retry attempt ${attempt}/${maxRetries} in ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // Validate content before processing
  static validateContent(content, type = 'url') {
    const errors = [];
    
    if (type === 'url') {
      if (!content || typeof content !== 'string') {
        errors.push('URL is required');
      } else if (!this.isValidUrl(content)) {
        errors.push('Please enter a valid URL');
      }
    } else if (type === 'text') {
      if (!content || typeof content !== 'string' || content.trim().length === 0) {
        errors.push('Text content is required');
      } else if (content.trim().length < 10) {
        errors.push('Text content must be at least 10 characters long');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  // Validate URL format
  static isValidUrl(string) {
    try {
      const url = new URL(string);
      return ['http:', 'https:'].includes(url.protocol);
    } catch {
      return false;
    }
  }
  
  // Handle offline/online state
  static handleConnectionState(isOnline, toast) {
    if (!isOnline) {
      this.showErrorToast({
        userMessage: 'You are offline. Some features may not work.',
        type: 'offline'
      }, toast);
    } else {
      this.showSuccessToast('Connection restored', toast);
    }
  }
  
  // Log error for debugging
  static logError(error, context = '', additionalData = {}) {
    const errorLog = {
      timestamp: new Date().toISOString(),
      context,
      error: {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      },
      additionalData,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    console.error('Error Log:', errorLog);
    
    // In production, you might want to send this to an error tracking service
    // like Sentry, LogRocket, or Bugsnag
  }
}

export default ErrorHandler;
